import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ENV_GROUP_LABEL,
  useDeviceGroups,
  useDevices,
  useHardwareTypes,
  useMe,
  useSimBatch,
  useTenants,
  type DeviceSimStatus,
  type EnvGroup,
} from '../api/hooks';
import {
  DeviceFilters,
  hasActiveFilters,
  type GroupFilter,
  type OnlineFilter,
} from '../components/DeviceFilters';
import { MoveModal } from '../components/MoveModal';
import { SimTopUpDialog } from '../components/SimTopUpDialog';
import { Skeleton } from '../components/Skeleton';
import { fmtBytes } from '../components/format';
import { fmtSysState } from '../components/ucEnums';
import { hardwareLabel, hardwareTitle, resolveHardware } from '../lib/hardware';
import { sortIndicator, useTableSearch } from '../components/useTableSearch';

type OpsSortKey =
  | 'thing'
  | 'friendly_name'
  | 'tenant_id'
  | 'serial'
  | 'hardware'   // DE-310
  | 'group'
  | 'online'
  | 'last_seen'
  | 'fw_ver'
  | 'mender'
  | 'sim'
  | 'sys_state'
  | 'created';

// DE-266 — one SIM-quota pill per fleet row. Mixed-carrier fleet: devices not
// managed through Onomondo (Telia SIM, no modem) render a muted dash, never a
// warning. Tooltip carries the used/total detail + freshness.
function SimQuotaPill({ d }: { d: DeviceSimStatus | undefined }) {
  if (!d || (d.error && d.error !== 'not-configured')) {
    const title =
      d?.error === 'not-onomondo' || d?.error === 'sim-not-found'
        ? 'Not an Onomondo-managed SIM'
        : d?.error === 'no-iccid'
          ? 'No SIM reported'
          : 'SIM quota unknown';
    return <span className="text-muted" title={title}>—</span>;
  }
  if (d.error === 'not-configured' || !d.sim) {
    return <span className="text-muted" title="SIM integration not configured">—</span>;
  }
  const q = d.sim.quota;
  if (!q) return <span className="pill-muted" title="No data cap set — uncapped">no cap</span>;
  const title =
    `${fmtBytes(q.usedBytes)} of ${fmtBytes(q.totalBytes)} used`
    + (q.resetsAt ? ` · resets ${new Date(q.resetsAt).toLocaleDateString()}` : '')
    + ` · fetched ${new Date(d.sim.fetchedAt).toLocaleTimeString()}`;
  if (q.blocked) return <span className="pill-warn" title={title}>capped</span>;
  const low =
    q.remainingBytes < q.totalBytes * 0.2 ||
    (q.alertThreshold != null && q.usedBytes >= q.alertThreshold);
  return (
    <span className={low ? 'pill-warn' : 'pill-accent'} title={title}>
      {fmtBytes(q.remainingBytes)} left
    </span>
  );
}

export function OpsDevices() {
  const me = useMe();
  const devices = useDevices();
  const groups = useDeviceGroups();
  // DE-310 — display names come from the registry, never from a formatter here.
  const hardwareTypes = useHardwareTypes();
  // DE-310 — thing → the deviceType THING ATTRIBUTE, flattened across every
  // group. Without this the table would pass only the shadow-derived column and
  // report as "assumed" 31 devices whose type is in fact KNOWN from the
  // attribute — under-claiming just as badly as over-claiming, and making the
  // assumed count meaningless. Caught by deploying and reading the real numbers.
  const attributeTypeOf = useMemo(() => {
    const out: Record<string, string> = {};
    for (const g of groups.data?.groups ?? []) Object.assign(out, g.device_types ?? {});
    return out;
  }, [groups.data]);
  const hardwareOf = (d: { thing: string; device_type: string | null }) =>
    resolveHardware(d.device_type, attributeTypeOf[d.thing], hardwareTypes.data?.hardwareTypes);
  const hardwareDisplayNameOf = (d: { thing: string; device_type: string | null }) =>
    hardwareOf(d).displayName;
  // Counts the RESOLVED source, not the absence of one column — otherwise it
  // would count devices whose type is known from the attribute.
  const assumedHardwareCount = (devices.data?.devices ?? [])
    .filter((d) => hardwareOf(d).assumed).length;
  const tenants = useTenants();
  const navigate = useNavigate();
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [onlineFilter, setOnlineFilter] = useState<OnlineFilter>('all');
  // DE-160 — unified move modal (group + optional tenant), shared with
  // /ops/device-groups. The state shape carries the device's current
  // env-group so the modal can render its "from" pill and filter the
  // group radio options.
  const [moveTarget, setMoveTarget] = useState<{
    thing: string;
    currentGroup: EnvGroup;
  } | null>(null);
  // DE-266 — bulk SIM data-cap dialog; acts on the currently filtered rows.
  const [simBulkOpen, setSimBulkOpen] = useState(false);

  // DE-266 — one batch quota lookup for the whole fleet (server caps at 100;
  // chunking can come when the fleet outgrows it). Key is the sorted thing
  // list, so filter changes reuse the same cached response.
  const allThings = useMemo(
    () => (devices.data?.devices ?? []).map((d) => d.thing).slice(0, 100),
    [devices.data],
  );
  const simBatch = useSimBatch(allThings, allThings.length > 0);
  const simOf = simBatch.data?.sims;

  if (me.data && me.data.role !== 'super-admin') {
    return (
      <div className="card max-w-md">
        <h2 className="font-semibold mb-1">Forbidden</h2>
        <p className="text-sm text-muted">
          The Ops view is restricted to super-admin.
        </p>
      </div>
    );
  }

  // Build thing → env-group map from the group-centric API response.
  const groupOf = new Map<string, EnvGroup>();
  for (const card of groups.data?.groups ?? []) {
    for (const thing of card.devices) groupOf.set(thing, card.name);
  }

  // Apply group + tenant + online filters BEFORE handing rows to the
  // search/sort hook so the filtered-count display is accurate.
  const filteredRows = useMemo(() => {
    const all = devices.data?.devices ?? [];
    return all.filter((d) => {
      if (groupFilter !== 'all' && groupOf.get(d.thing) !== groupFilter) return false;
      if (tenantFilter !== 'all' && d.tenant_id !== tenantFilter) return false;
      if (onlineFilter === 'online' && !d.online) return false;
      if (onlineFilter === 'offline' && d.online) return false;
      return true;
    });
    // groupOf is rebuilt every render from groups.data — depend on its source
    // to avoid stale-closure issues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices.data, groups.data, groupFilter, tenantFilter, onlineFilter]);

  const table = useTableSearch(filteredRows, {
    searchAccessors: [
      (d) => d.thing,
      (d) => d.friendly_name,
      (d) => d.tenant_id,
      (d) => d.serial,
      // DE-310 — searchable by BOTH spellings: an operator types what the
      // column shows (R1035-10) or what the artifact says (artomeui-r100x).
      (d) => d.device_type,
      (d) => hardwareDisplayNameOf(d),
      (d) => d.fw_ver,
      (d) => d.mender_artifact,
    ],
    sortAccessors: {
      thing: (d) => d.thing,
      friendly_name: (d) => d.friendly_name,
      tenant_id: (d) => d.tenant_id,
      serial: (d) => d.serial,
      // DE-310 — sorts on the DISPLAY name so the column reads in the order
      // it is written, with assumptions after facts for the same board.
      hardware: (d) => `${hardwareDisplayNameOf(d)}${hardwareOf(d).assumed ? ' ~' : ''}`,
      group: (d) => groupOf.get(d.thing) ?? null,
      online: (d) => (d.online ? 1 : 0),
      last_seen: (d) => (d.last_seen ? new Date(d.last_seen).getTime() : null),
      fw_ver: (d) => d.fw_ver,
      mender: (d) => d.mender_artifact,
      // Uncapped/unmanaged sort as null (last); capped-out devices sort first
      // ascending, so "who needs data" is one click.
      sim: (d) => simOf?.[d.thing]?.sim?.quota?.remainingBytes ?? null,
      sys_state: (d) => d.sys_state,
      created: (d) => new Date(d.created_at).getTime(),
    } satisfies Record<
      OpsSortKey,
      (d: NonNullable<typeof devices.data>['devices'][number]) => string | number | null
    >,
    // Default = Thing ascending. Stable lexicographic order that doesn't
    // shift as devices come on/off line; matches the Dashboard default.
    initialSort: { key: 'thing' as OpsSortKey, dir: 'asc' as const },
  });
  const headers: Array<{ key: OpsSortKey; label: string }> = [
    { key: 'thing', label: 'Thing' },
    { key: 'friendly_name', label: 'Name' },
    { key: 'tenant_id', label: 'Tenant' },
    { key: 'serial', label: 'Serial' },
    { key: 'hardware', label: 'Hardware' },
    { key: 'group', label: 'Group' },
    { key: 'online', label: 'Online' },
    { key: 'last_seen', label: 'Last seen' },
    { key: 'fw_ver', label: 'FW' },
    { key: 'mender', label: 'Mender' },
    { key: 'sim', label: 'SIM data' },
    { key: 'sys_state', label: 'sys_state' },
    { key: 'created', label: 'Created' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">All devices</h1>
          <p className="text-sm text-muted">
            Every tenant, every device. Live shadow + last-seen.
            {/* DE-310 — how much of the Hardware column is inference rather than
                fact. It falls as DE-308 telemetry reaches the fleet; at zero the
                assumption can become a warning, which is a one-line change in
                lib/hardware.ts rather than new work. */}
            {assumedHardwareCount > 0 && (
              <>
                {' '}
                <span className="text-warn" title="These devices have reported no hardware type and carry no IoT thing attribute, so the fleet default is shown. It is not a fact.">
                  {assumedHardwareCount} of {devices.data?.devices.length ?? 0} hardware values are
                  assumed
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* DE-266 — bulk cap-set on whatever the filters currently show. */}
          <button
            type="button"
            onClick={() => setSimBulkOpen(true)}
            disabled={table.rows.length === 0}
            className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft disabled:opacity-50"
            title="Set the Onomondo data cap on every device currently listed below"
          >
            Set SIM data limit…
          </button>
          <Link to="/ops/device-groups" className="text-accent text-sm hover:underline">
            Manage groups →
          </Link>
        </div>
      </div>

      <div className="card overflow-x-auto">
        {devices.isLoading && (
          <table className="w-full text-sm" aria-label="Loading devices">
            <tbody>
              <Skeleton.TableRows rows={6} cols={12} />
            </tbody>
          </table>
        )}
        {devices.data && (
          <>
            <DeviceFilters
              search={table.search}
              setSearch={table.setSearch}
              groupFilter={groupFilter}
              setGroupFilter={setGroupFilter}
              tenantFilter={tenantFilter}
              setTenantFilter={setTenantFilter}
              onlineFilter={onlineFilter}
              setOnlineFilter={setOnlineFilter}
              tenants={tenants.data?.tenants ?? []}
              showTenant={true}
              searchPlaceholder="Search thing, name, tenant, serial, FW, Mender…"
              refresh={() => void devices.refetch()}
              isFetching={devices.isFetching}
              endSlot={
                hasActiveFilters(
                  table.search,
                  groupFilter,
                  tenantFilter,
                  onlineFilter,
                ) && (
                  <span className="text-xs text-muted">
                    {table.rows.length} of {devices.data.devices.length} match
                  </span>
                )
              }
            />
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted border-b border-border">
                  {headers.map((h) => (
                    <th
                      key={h.key}
                      className="font-medium py-2 pr-4 cursor-pointer select-none hover:text-fg"
                      onClick={() => table.toggleSort(h.key)}
                    >
                      {h.label}{sortIndicator(table.sort, h.key)}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {table.rows.map((d) => {
                  const group = groupOf.get(d.thing);
                  const href = `/devices/${encodeURIComponent(d.thing)}`;
                  const go = () => navigate(href);
                  return (
                    <tr
                      key={d.thing}
                      onClick={go}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          go();
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      aria-label={`Open ${d.friendly_name ?? d.thing}`}
                      className={`group/row border-b border-border last:border-0 cursor-pointer transition-colors hover:bg-soft focus:outline-none focus-visible:bg-soft focus-visible:ring-1 focus-visible:ring-accent ${d.online ? '' : 'opacity-60'}`}
                    >
                      <td className="py-2 pr-4 font-mono text-xs">{d.thing}</td>
                      <td className="py-2 pr-4 text-xs">
                        {d.friendly_name ?? <span className="text-muted">—</span>}
                      </td>
                      <td className="py-2 pr-4"><span className="pill-muted">{d.tenant_id}</span></td>
                      <td className="py-2 pr-4 font-mono text-xs">{d.serial ?? '—'}</td>
                      {/* DE-310 — an assumption is rendered differently from a
                          fact. Muted + italic, never the same weight as a value
                          the device actually reported. */}
                      <td className="py-2 pr-4 text-xs">
                        {(() => {
                          const hw = hardwareOf(d);
                          return (
                            <span
                              className={hw.assumed ? 'text-muted italic' : ''}
                              title={hardwareTitle(hw)}
                            >
                              {hardwareLabel(hw)}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="py-2 pr-4 text-xs">
                        {group ? (
                          <span className="pill-muted">{ENV_GROUP_LABEL[group]}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={d.online ? 'pill-accent' : 'pill-muted'}>
                          {d.online ? 'online' : 'offline'}
                        </span>
                      </td>
                      <td className="py-2 pr-4 tabular-nums">
                        {d.last_seen ? new Date(d.last_seen).toLocaleString() : '—'}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">{d.fw_ver ?? '—'}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{d.mender_artifact ?? '—'}</td>
                      <td className="py-2 pr-4 text-xs">
                        <SimQuotaPill d={simOf?.[d.thing]} />
                      </td>
                      <td className="py-2 pr-4 text-xs">{fmtSysState(d.sys_state)}</td>
                      <td className="py-2 pr-4 tabular-nums">
                        {new Date(d.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-2 pr-1 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          {/* DE-160 — opens the shared MoveModal (group +
                              optional tenant). Disabled until useDeviceGroups
                              has loaded so the modal always has a known
                              currentGroup to render and filter against;
                              that's typically a sub-second window after
                              the page first paints. */}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!group) return;
                              setMoveTarget({ thing: d.thing, currentGroup: group });
                            }}
                            disabled={!group}
                            className="px-2 py-1 text-xs rounded-md border border-border text-muted hover:bg-white hover:text-fg focus:outline-none focus-visible:ring-1 focus-visible:ring-accent disabled:opacity-40 disabled:cursor-not-allowed"
                            title={group ? 'Move to another group / tenant' : 'Group not yet loaded'}
                          >
                            Move
                          </button>
                          <span
                            className="text-muted opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-visible/row:opacity-100"
                            aria-hidden="true"
                          >
                            ›
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>

      {moveTarget && (
        <MoveModal
          thing={moveTarget.thing}
          currentGroup={moveTarget.currentGroup}
          onClose={() => setMoveTarget(null)}
        />
      )}

      {simBulkOpen && (
        <SimTopUpDialog
          target={{ mode: 'bulk', things: table.rows.map((d) => d.thing) }}
          onClose={() => setSimBulkOpen(false)}
        />
      )}
    </div>
  );
}

